import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useCallback, useMemo } from 'react';

import {
  type CanaryProductAreaCompanyScore,
  type CanaryProductAreaVisibilityScore,
  useCanaryVisibilityScoreByProductArea,
} from '@/api/aiVisibility';
import { useCurrentInstance } from '@/api/organization';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import { Panel } from '@/components/panels';
import { Badge } from '@/components/typography/Badge';
import { numberFormat } from '@/helper/numberFormatter';

const EMPTY_PRODUCT_AREAS: CanaryProductAreaVisibilityScore[] = [];

const compareCompanies = (a: CanaryProductAreaCompanyScore, b: CanaryProductAreaCompanyScore) =>
  a.company.localeCompare(b.company) || a.domain.localeCompare(b.domain);

const companyKey = (company: CanaryProductAreaCompanyScore) =>
  JSON.stringify([company.company, company.domain]);

const AIVisibilityProductAreaTable = () => {
  const { scores, isLoading, error } = useCanaryVisibilityScoreByProductArea();
  const { instance } = useCurrentInstance();
  const productAreas = scores ?? EMPTY_PRODUCT_AREAS;
  const brandDomain = instance?.domain?.toLowerCase();
  const isBrand = useCallback(
    (domain: string) => domain.toLowerCase() === brandDomain,
    [brandDomain],
  );
  const { companies, overallLeader } = useMemo(() => {
    const byCompany = new Map<string, CanaryProductAreaCompanyScore>();
    let leader: CanaryProductAreaCompanyScore | null = null;
    for (const area of productAreas) {
      for (const company of area.companies) {
        byCompany.set(companyKey(company), company);
        if (
          company.visibilityScore != null &&
          (leader == null ||
            leader.visibilityScore == null ||
            company.visibilityScore > leader.visibilityScore ||
            (company.visibilityScore === leader.visibilityScore &&
              compareCompanies(company, leader) < 0))
        ) {
          leader = company;
        }
      }
    }
    return { companies: [...byCompany.values()].sort(compareCompanies), overallLeader: leader };
  }, [productAreas]);
  const productAreaLabel = t`Product area`;
  const categoryLeaderLabel = t`Category leader`;

  const columns = useMemo<DataTableColumnDef<CanaryProductAreaVisibilityScore>[]>(
    () => [
      {
        id: 'productArea',
        accessorKey: 'productArea',
        header: productAreaLabel,
        size: 250,
        flex: 2,
        cell: ({ row }) => <span className="font-semibold">{row.original.productArea}</span>,
      },
      ...companies.map((company): DataTableColumnDef<CanaryProductAreaVisibilityScore> => ({
        id: companyKey(company),
        header: () => (
          <span>
            {company.company}{' '}
            {isBrand(company.domain) && (
              <span className="font-normal">
                (<Trans>you</Trans>)
              </span>
            )}
          </span>
        ),
        accessorFn: (area) =>
          area.companies.find(
            (candidate) =>
              candidate.company === company.company && candidate.domain === company.domain,
          )?.visibilityScore,
        size: 125,
        flex: 1,
        cell: ({ getValue }) => {
          const score = getValue<number | null | undefined>();
          return score == null ? '—' : numberFormat(score, { precision: 0 });
        },
      })),
      {
        id: 'categoryLeader',
        accessorKey: 'categoryLeader',
        header: categoryLeaderLabel,
        size: 170,
        cell: ({ row }) => {
          const leader = row.original.categoryLeader;
          if (leader == null) return '—';
          const isBrandLeader = row.original.companies.some(
            (company) => company.company === leader && isBrand(company.domain),
          );
          return (
            <Badge color={isBrandLeader ? 'success' : 'critical'} size="small">
              {leader}
            </Badge>
          );
        },
      },
    ],
    [companies, isBrand, productAreaLabel, categoryLeaderLabel],
  );

  return (
    <Panel
      title={<Trans>Product area</Trans>}
      isFullHeight
      subtitle={
        <Trans>
          Product visibility across all tracked LLMs, within your product areas from 0–100.
        </Trans>
      }
      actions={
        !isLoading &&
        !error &&
        overallLeader && (
          <div className="flex items-center gap-2 text-sm">
            <span>
              <Trans>Overall category leader</Trans>
            </span>
            <Badge color={isBrand(overallLeader.domain) ? 'success' : 'critical'} size="small">
              {overallLeader.company}
            </Badge>
          </div>
        )
      }
    >
      <DataTable<CanaryProductAreaVisibilityScore>
        columns={columns}
        data={productAreas}
        loading={isLoading}
        error={error}
        emptyMessage={<Trans>No product area visibility scores available.</Trans>}
        pagination={false}
        initialColumnPinning={{ start: ['productArea'] }}
        getRowId={(area) => area.productArea}
      />
    </Panel>
  );
};

export default AIVisibilityProductAreaTable;
